/** Standard square meters per acre */
export const M2_PER_ACRE = 4_046.856_422_4;

export const COUNT_PER_ACRE_TOOLTIP =
  "Extrapolated from the sample density as if the entire acre were planted with no gaps";

/**
 * Convert count per square meter to count per acre.
 * Simple linear extrapolation: countPerM2 × m²/acre.
 */
export function countPerSquareMeterToPerAcre(countPerM2: number): number {
  return countPerM2 * M2_PER_ACRE;
}

/**
 * Format count per acre for display — rounded to nearest thousand.
 * e.g., 30351 → "30k"
 */
export function formatCountPerAcre(countPerAcre: number): string {
  return `${Math.round(countPerAcre / 1000)}k`;
}

export function formatRoundedCountPerSquareMeterPerAcre(countPerM2: number): string {
  return formatCountPerAcre(countPerSquareMeterToPerAcre(Math.round(countPerM2)));
}

export interface WeightedStandCountEntry {
  countPerSquareMeter?: number;
  objectCount?: number;
  boxLengthM?: number;
  boxWidthM?: number;
}

export interface WeightedStandCountParts {
  totalCount: number;
  totalArea: number;
  validEntries: number;
}

function getConfiguredBoxArea(entry: WeightedStandCountEntry): number | null {
  if (entry.boxLengthM == null || entry.boxWidthM == null) return null;
  if (entry.boxLengthM <= 0 || entry.boxWidthM <= 0) return null;
  return entry.boxLengthM * entry.boxWidthM;
}

export function getEffectiveStandCountArea(entry: WeightedStandCountEntry): number | null {
  const configuredArea = getConfiguredBoxArea(entry);
  if (configuredArea == null) return null;

  if (
    entry.objectCount != null &&
    entry.objectCount >= 0 &&
    entry.countPerSquareMeter != null &&
    entry.countPerSquareMeter > 0
  ) {
    return entry.objectCount / entry.countPerSquareMeter;
  }

  return configuredArea;
}

export function computeWeightedStandCountParts(entries: WeightedStandCountEntry[]): WeightedStandCountParts {
  return entries.reduce<WeightedStandCountParts>(
    (result, entry) => {
      const area = getEffectiveStandCountArea(entry);
      if (area == null || area <= 0) return result;

      if (entry.objectCount != null) {
        result.totalCount += entry.objectCount;
      } else if (entry.countPerSquareMeter != null) {
        result.totalCount += entry.countPerSquareMeter * area;
      } else {
        return result;
      }

      result.totalArea += area;
      result.validEntries += 1;
      return result;
    },
    { totalCount: 0, totalArea: 0, validEntries: 0 }
  );
}

/**
 * Compute properly weighted count per square meter across multiple samples.
 * Weights each sample's count/m² by its box area, so larger boxes contribute more.
 * Equivalent to: sum(count_i) / sum(area_i) where count_i = countPerM2_i × area_i.
 * If both objectCount and countPerSquareMeter are present, the saved density is
 * treated as authoritative and the effective area is derived from count/density.
 * Returns null if no valid entries.
 */
export function computeWeightedCountPerM2(entries: WeightedStandCountEntry[]): number | null {
  const parts = computeWeightedStandCountParts(entries);
  return parts.totalArea > 0 ? parts.totalCount / parts.totalArea : null;
}
